import { useState } from "preact/hooks"
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button.tsx"
import { Input } from "@/components/ui/input.tsx"
import { Label } from "@/components/ui/label.tsx"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx"
import { DeductionDetailJson } from "@/api/deduction.tsx"
import {
  AUTO_COMPLETE_TRIGGERS,
  ChecklistResponse,
  ChecklistScope,
  ChecklistTemplateResponse,
  create_checklist,
  delete_checklist,
  list_checklist_templates,
  update_checklist,
} from "@/api/checklist.tsx"
import { UserState } from "@/auth/user.tsx"
import { useFetch } from "@/utils"

interface StepDraft {
  id?: string
  title: string
  auto_complete_trigger: string | null
}

interface CreateChecklistDialogProps {
  deduction: DeductionDetailJson
  existingChecklist?: ChecklistResponse
  onClose: () => void
  onCreated: () => void
}

export function CreateChecklistDialog({
  deduction,
  existingChecklist,
  onClose,
  onCreated,
}: CreateChecklistDialogProps) {
  const user = UserState.use()
  const isSystemAdmin = user?.role === "system_admin"
  const isEditing = !!existingChecklist

  const [name, setName] = useState(existingChecklist?.checklist.name ?? "")
  const [steps, setSteps] = useState<StepDraft[]>(
    existingChecklist
      ? existingChecklist.steps.map((s) => ({
          id: s.id,
          title: s.title,
          auto_complete_trigger: s.auto_complete_trigger,
        }))
      : [{ title: "", auto_complete_trigger: null }]
  )
  const [scope, setScope] = useState<ChecklistScope>("deduction")
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: templates } = useFetch(
    () => list_checklist_templates(),
    [],
    []
  )

  function handleAddStep() {
    setSteps([...steps, { title: "", auto_complete_trigger: null }])
  }

  function handleRemoveStep(index: number) {
    setSteps(steps.filter((_: StepDraft, i: number) => i !== index))
  }

  function handleStepChange(index: number, field: keyof StepDraft, value: string | null) {
    setSteps(
      steps.map((s: StepDraft, i: number) =>
        i === index ? { ...s, [field]: value } : s
      )
    )
  }

  function handleMoveStep(index: number, direction: "up" | "down") {
    const newSteps = [...steps]
    const targetIndex = direction === "up" ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= newSteps.length) return
    const temp = newSteps[index]
    newSteps[index] = newSteps[targetIndex]
    newSteps[targetIndex] = temp
    setSteps(newSteps)
  }

  function handleApplyTemplate(templateId: string) {
    const template = templates.find((t: ChecklistTemplateResponse) => t.checklist.id === templateId)
    if (!template) return

    setName(template.checklist.name)
    setSteps(
      template.steps.map((s) => ({
        title: s.title,
        auto_complete_trigger: s.auto_complete_trigger,
      }))
    )
  }

  async function handleSave() {
    const validSteps = steps.filter((s: StepDraft) => s.title.trim())
    if (!name.trim()) {
      setError("Please enter a checklist name")
      return
    }
    if (validSteps.length === 0) {
      setError("Please add at least one step")
      return
    }

    setSaving(true)
    setError(null)

    try {
      if (isEditing && existingChecklist) {
        const res = await update_checklist(existingChecklist.checklist.id, {
          name: name.trim(),
          steps: validSteps.map((s: StepDraft, i: number) => ({
            id: s.id,
            title: s.title.trim(),
            auto_complete_trigger: s.auto_complete_trigger,
            position: i + 1,
          })),
        })
        if (!res.ok) {
          setError(res.error.message)
          return
        }
      } else {
        const res = await create_checklist({
          name: name.trim(),
          steps: validSteps.map((s: StepDraft) => ({
            title: s.title.trim(),
            auto_complete_trigger: s.auto_complete_trigger,
          })),
          scope,
          deduction_id: scope === "deduction" ? deduction.id : undefined,
          category_ids:
            (scope === "category" || scope === "system_category") && deduction.category_id
              ? [deduction.category_id]
              : undefined,
        })
        if (!res.ok) {
          setError(res.error.message)
          return
        }
      }
      onCreated()
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!existingChecklist) return
    setDeleting(true)
    try {
      const res = await delete_checklist(existingChecklist.checklist.id)
      if (!res.ok) {
        setError(res.error.message)
        return
      }
      onCreated()
    } finally {
      setDeleting(false)
    }
  }

  const categoryName = deduction.category || "Uncategorized"
  const categoryDescription = deduction.category_description
  const categoryLabel = categoryDescription
    ? `${categoryName} — ${categoryDescription}`
    : categoryName

  return (
    <Dialog open onOpenChange={(open: boolean) => !open && onClose()}>
      {/* @ts-ignore */}
      <DialogContent class="sm:max-w-[520px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Checklist" : "Create Validation Checklist"}</DialogTitle>
        </DialogHeader>

        <div class="space-y-4">
          {/* Template picker (only for new checklists) */}
          {!isEditing && templates.length > 0 && (
            <div>
              <Label>Start from template</Label>
              <Select
                onValueChange={(val: string) => handleApplyTemplate(val)}
                value=""
              >
                {/* @ts-ignore */}
                <SelectTrigger class="mt-1">
                  <SelectValue placeholder="Choose a template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t: ChecklistTemplateResponse) => (
                    <SelectItem key={t.checklist.id} value={t.checklist.id}>
                      {t.checklist.name} ({t.steps.length} steps)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Checklist name */}
          <div>
            <Label>Checklist name</Label>
            <Input
              value={name}
              onInput={(e: Event) => setName((e.target as HTMLInputElement).value)}
              placeholder="e.g. Promotional Verification Checklist"
              class="mt-1"
            />
          </div>

          {/* Steps */}
          <div>
            <Label>Steps</Label>
            <div class="space-y-2 mt-1">
              {steps.map((step: StepDraft, index: number) => (
                <StepEditor
                  key={index}
                  step={step}
                  index={index}
                  total={steps.length}
                  onChange={(field: keyof StepDraft, value: string | null) =>
                    handleStepChange(index, field, value)
                  }
                  onRemove={() => handleRemoveStep(index)}
                  onMove={(dir: "up" | "down") => handleMoveStep(index, dir)}
                />
              ))}
            </div>
            <Button
              variant="outline"
              // @ts-ignore
              size="sm"
              onClick={handleAddStep}
              class="mt-2"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add step
            </Button>
          </div>

          {/* Scope selector (only for new checklists) */}
          {!isEditing && (
            <div>
              <Label>Apply checklist to</Label>
              <Select
                onValueChange={(val: string) => setScope(val as ChecklistScope)}
                value={scope}
              >
                {/* @ts-ignore */}
                <SelectTrigger class="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="deduction">This deduction only</SelectItem>
                  <SelectItem value="category">
                    All "{categoryLabel}" deductions for your brand
                  </SelectItem>
                  {isSystemAdmin && (
                    <SelectItem value="system_category">
                      All "{categoryLabel}" deductions (all organizations - system admin only)
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p class="text-xs text-slate-400 mt-1">
                {scope === "deduction" && "This checklist will only appear on this deduction."}
                {scope === "category" &&
                  `This checklist will appear on all "${categoryLabel}" deductions for your organization.`}
                {scope === "system_category" &&
                  `This checklist will appear on all "${categoryLabel}" deductions across all organizations.`}
              </p>
            </div>
          )}

          {error && <p class="text-sm text-red-600">{error}</p>}
        </div>

        <DialogFooter>
          <div class="flex justify-between w-full">
            <div>
              {isEditing && (
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  disabled={deleting}
                  class="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  {deleting ? "Deleting..." : "Delete"}
                </Button>
              )}
            </div>
            <div class="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : isEditing ? "Save Changes" : "Create Checklist"}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface StepEditorProps {
  step: StepDraft
  index: number
  total: number
  onChange: (field: keyof StepDraft, value: string | null) => void
  onRemove: () => void
  onMove: (direction: "up" | "down") => void
}

function StepEditor({ step, index, total, onChange, onRemove, onMove }: StepEditorProps) {
  const [showTrigger, setShowTrigger] = useState(!!step.auto_complete_trigger)

  return (
    <div class="flex items-start gap-2 p-2 bg-slate-50 rounded border border-slate-200">
      <span class="text-xs text-slate-400 font-mono mt-2.5 w-4 text-center shrink-0">
        {index + 1}
      </span>
      <div class="flex-1 space-y-1.5">
        <Input
          value={step.title}
          onInput={(e: Event) => onChange("title", (e.target as HTMLInputElement).value)}
          placeholder="Step description..."
          // @ts-ignore
          size="sm"
          class="text-sm"
        />
        {showTrigger ? (
          <div class="flex items-center gap-1.5">
            <Select
              onValueChange={(val: string) =>
                onChange("auto_complete_trigger", val === "none" ? null : val)
              }
              value={step.auto_complete_trigger ?? "none"}
            >
              {/* @ts-ignore */}
              <SelectTrigger class="h-7 text-xs">
                <SelectValue placeholder="Auto-complete when..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No auto-complete</SelectItem>
                {AUTO_COMPLETE_TRIGGERS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              onClick={() => {
                setShowTrigger(false)
                onChange("auto_complete_trigger", null)
              }}
              class="text-slate-400 hover:text-slate-600 text-xs cursor-pointer"
            >
              Remove
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowTrigger(true)}
            class="text-xs text-blue-500 hover:text-blue-600 cursor-pointer"
          >
            + Add auto-complete trigger
          </button>
        )}
      </div>
      <div class="flex flex-col gap-0.5 shrink-0">
        <button
          onClick={() => onMove("up")}
          disabled={index === 0}
          class="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30 cursor-pointer disabled:cursor-default"
        >
          <ArrowUp className="h-3 w-3" />
        </button>
        <button
          onClick={() => onMove("down")}
          disabled={index === total - 1}
          class="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30 cursor-pointer disabled:cursor-default"
        >
          <ArrowDown className="h-3 w-3" />
        </button>
      </div>
      <button
        onClick={onRemove}
        class="p-1 text-slate-400 hover:text-red-500 mt-1.5 cursor-pointer"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
